import type { ReactNode } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '@/internationalization/cn'

export interface Column<T> {
  id: string
  header: ReactNode
  cell: (row: T) => ReactNode
  /** Tailwind width class, e.g. 'w-40'. Omit to auto-size. */
  width?: string
  align?: 'left' | 'right' | 'center'
  sortable?: boolean
  /** Columns hidden below md — the ones a phone can do without. */
  hideOnMobile?: boolean
}

export interface TableProps<T> {
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
  sort?: { columnId: string; direction: 'asc' | 'desc' }
  onSortChange?: (columnId: string) => void
  /** Rendered instead of the body when rows is empty. */
  empty?: ReactNode
  isLoading?: boolean
  className?: string
}

/**
 * A dense, sortable table for desktop.
 *
 * Below md, hide non-essential columns with `hideOnMobile` — and if that still
 * leaves more than three, render a card list instead. A table that scrolls
 * sideways on a phone is a table nobody reads.
 */
export function Table<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  sort,
  onSortChange,
  empty,
  isLoading,
  className,
}: TableProps<T>) {
  const visible = columns

  return (
    <div className={cn('w-full overflow-x-auto', className)}>
      <table className="w-full border-collapse text-base">
        <thead>
          <tr className="border-b border-border bg-surface-muted/60">
            {visible.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={cn(
                  'px-3 py-2.5 text-2xs font-semibold uppercase tracking-wide text-fg-subtle',
                  column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left',
                  column.width,
                  column.hideOnMobile && 'hidden md:table-cell',
                )}
              >
                {column.sortable && onSortChange ? (
                  <button
                    type="button"
                    onClick={() => onSortChange(column.id)}
                    className="inline-flex items-center gap-1 hover:text-fg"
                  >
                    {column.header}
                    {sort?.columnId === column.id &&
                      (sort.direction === 'asc' ? (
                        <ChevronUp aria-hidden className="h-3 w-3" />
                      ) : (
                        <ChevronDown aria-hidden className="h-3 w-3" />
                      ))}
                  </button>
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, index) => (
              <tr key={`skeleton-${index}`} className="border-b border-border last:border-0">
                {visible.map((column) => (
                  <td
                    key={column.id}
                    className={cn('px-3 py-3', column.hideOnMobile && 'hidden md:table-cell')}
                  >
                    <span className="block h-3.5 w-full max-w-40 animate-pulse rounded bg-surface-muted" />
                  </td>
                ))}
              </tr>
            ))}

          {!isLoading &&
            rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'border-b border-border last:border-0 transition-colors duration-fast',
                  onRowClick && 'cursor-pointer hover:bg-surface-muted',
                )}
              >
                {visible.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      'px-3 py-2.5 align-middle text-fg',
                      column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left',
                      column.hideOnMobile && 'hidden md:table-cell',
                    )}
                  >
                    {column.cell(row)}
                  </td>
                ))}
              </tr>
            ))}

          {!isLoading && rows.length === 0 && empty && (
            <tr>
              <td colSpan={visible.length} className="px-3 py-10">
                {empty}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
